import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

export default function ProtectedRoute({ children }) {
  const { user, loading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [authState, setAuthState] = useState('checking'); // 'checking', 'authenticated', 'unauthenticated'
  const hasChecked = useRef(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Cleanup timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // If we have a session_id in the hash, let AuthCallback handle it
    if (location.hash?.includes('session_id=')) {
      return;
    }

    // If user data came from navigation state (just logged in), trust it
    if (location.state?.user) {
      setAuthState('authenticated');
      return;
    }

    // If still loading initial auth check, wait (but with a timeout)
    if (loading) {
      // Set a fallback timeout - if loading takes too long, assume unauthenticated
      if (!timeoutRef.current) {
        timeoutRef.current = setTimeout(() => {
          console.log('[ProtectedRoute] Auth check timeout - redirecting to login');
          setAuthState('unauthenticated');
          navigate('/login', { replace: true });
        }, 5000);
      }
      return;
    }

    // Clear timeout if we finished loading
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // If we have a user, we're authenticated
    if (user) {
      setAuthState('authenticated');
      hasChecked.current = true;
      return;
    }

    // No user and not loading - try one auth check before giving up
    if (!hasChecked.current) {
      hasChecked.current = true;
      // Try one more auth check before giving up
      checkAuth().then((result) => {
        // If auth check was successful and we have a user, effect will re-run
        // If it failed or was skipped, redirect to login
        if (result && !result.success && !result.skipped) {
          setAuthState('unauthenticated');
          navigate('/login', { replace: true });
        }
      });
      return;
    }

    // Truly not authenticated - redirect to login
    setAuthState('unauthenticated');
    navigate('/login', { replace: true });
  }, [user, loading, navigate, location.state, location.hash, checkAuth]);

  if (authState === 'checking' || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 dark:bg-stone-950">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
          <p className="text-stone-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (authState === 'unauthenticated') {
    return null;
  }

  return children;
}
